import { Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { resolveAsset } from '../blog/cdn';

/**
 * Palette for letter fallbacks. Picked by hashing the title so a given post
 * always gets the same colour, rather than shuffling between renders.
 */
const FALLBACK_COLORS = ['#2e7d32', '#00695c', '#37474f', '#4e342e', '#455a64', '#33691e'];

const hash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

/** A diameter in px, or one per breakpoint. */
type Size = number | Partial<Record<'xs' | 'sm' | 'md', number>>;

// Only the theme-constant styling lives here. The diameter is per-instance and
// may be responsive, so it goes through `sx` instead of a transient prop.
const Circle = styled(Avatar, {
  shouldForwardProp: (p) => p !== '$bg',
})<{ $bg: string }>(({ theme, $bg }) => ({
  backgroundColor: $bg,
  color: theme.palette.common.white,
  fontWeight: 600,
  flexShrink: 0,
  boxShadow: theme.shadows[3],
}));

/** Expand a Size into responsive width/height/fontSize values for `sx`. */
const sizeSx = (size: Size) => {
  if (typeof size === 'number') {
    return { width: size, height: size, fontSize: size / 2.4 };
  }
  const map = <T,>(fn: (n: number) => T) =>
    Object.fromEntries(
      Object.entries(size).map(([bp, n]) => [bp, fn(n as number)]),
    );
  return { width: map((n) => n), height: map((n) => n), fontSize: map((n) => n / 2.4) };
};

type BlogAvatarProps = {
  name: string;
  /** Blog slug, used to resolve a relative image path against the CDN. */
  slug: string;
  image?: string;
  /** Pixel diameter, or a per-breakpoint map. */
  size?: Size;
  className?: string;
};

/**
 * The circular image that represents a post in listings and on its detail page.
 * Falls back to the title's first letter when the post has no image, which is
 * the case for text-only posts like poetry.
 */
const BlogAvatar: React.FC<BlogAvatarProps> = ({ name, slug, image, size = 72, className }) => {
  const src = image ? resolveAsset(slug, image) : undefined;
  const letter = name.trim().charAt(0).toUpperCase() || '?';
  const bg = FALLBACK_COLORS[hash(name) % FALLBACK_COLORS.length];

  return (
    <Circle className={className} $bg={bg} src={src} alt={name} variant="circular" sx={sizeSx(size)}>
      {letter}
    </Circle>
  );
};

export default BlogAvatar;
